import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useModuleVisibility } from "../../hooks/useModuleVisibility";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Export, ClockCounterClockwise, CopySimple } from "@phosphor-icons/react";
import { FORMATION_TEMPLATES, getTemplatesForSport } from "./components/formationTemplates";
import LeopartLoader from "../../shared/LeopartLoader/LeopartLoader";
import FormationBoard from "./components/FormationBoard";
import PlayerCard from "./components/PlayerCard";
import SubstitutionsTab from "./components/SubstitutionsTab";
import PostMatchTab from "./components/PostMatchTab";
import styles from "./MatchPage.module.scss";
import { useEndpoints } from "../../hooks/useEndpoints";

type MatchTab = "formazione" | "cambi" | "post-gara";

const TAB_CONFIG: Record<MatchTab, { label: string; visibilityKey: string }> = {
  formazione: { label: "Formazione", visibilityKey: "match-formazione" },
  cambi: { label: "Cambi", visibilityKey: "match-cambi" },
  "post-gara": { label: "Post-gara", visibilityKey: "match-post-gara" },
};

/**
 * Vista Match domenicale — layout fullscreen 3 colonne.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 634-807.
 * Route: /dashboard/teams/:teamId/matches/:matchId
 */
const MatchPage = () => {
  const { matchId, teamId } = useParams<{ matchId: string; teamId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const endpoints = useEndpoints();
  const { isTabVisible } = useModuleVisibility();

  // Tab visibili secondo i feature-flag runtime
  const visibleTabs = useMemo(
    () => (Object.keys(TAB_CONFIG) as MatchTab[]).filter(
      (t) => isTabVisible(TAB_CONFIG[t].visibilityKey)
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible]
  );

  const [activeTab, setActiveTab] = useState<MatchTab>("formazione");

  // Se il tab attivo viene nascosto da un flag, spostati al primo visibile
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTabs.join(','), activeTab]);

  // ─────────────────── Fetch match session ──────────────────────
  const { data: match, isLoading, error } = useQuery({
    queryKey: ["matchSession", matchId],
    queryFn: () =>
      endpoints
        .getMatchSession(matchId!)
        .then((r) => r.data),
    enabled: !!matchId,
  });

  // ─────────────────── Fetch team members ───────────────────────
  const { data: teamData } = useQuery({
    queryKey: ["team", teamId],
    queryFn: () =>
      endpoints.getTeam(teamId!).then((r) => r.data),
    enabled: !!teamId,
  });

  const teamPlayers: Array<{ _id: string; firstName: string; lastName: string }> =
    teamData?.users ?? [];

  // ─────────────────── Mutations ────────────────────────────────
  const invalidateMatch = () =>
    queryClient.invalidateQueries({ queryKey: ["matchSession", matchId] });

  const addSubstitutionMut = useMutation({
    mutationFn: (sub: {
      minute?: number;
      playerIn: string;
      playerOut: string;
      reason?: string;
    }) =>
      endpoints
        .addMatchSubstitution(matchId!, sub)
        .then((r) => r.data),
    onSuccess: invalidateMatch,
  });

  const savePostMatchMut = useMutation({
    mutationFn: (data: {
      result?: { homeScore?: number; awayScore?: number; outcome?: string };
      postMatchNotes?: string;
      matchStats?: Record<string, number>;
    }) =>
      endpoints.savePostMatch(matchId!, data).then((r) => r.data),
    onSuccess: invalidateMatch,
  });

  const recalcMut = useMutation({
    mutationFn: (totalMinutes: number) =>
      endpoints
        .recalcMinutesPlayed(matchId!, totalMinutes)
        .then((r) => r.data),
    onSuccess: invalidateMatch,
  });

  // ─────────────────── Storico formazioni stagione ───────────────
  const [historyOpen, setHistoryOpen] = useState(false);
  const { data: lineupHistory = [] } = useQuery({
    queryKey: ["lineupHistory", matchId, teamId],
    queryFn: () =>
      endpoints
        .getLineupHistory(matchId!, { teamId: teamId!, limit: 10 })
        .then((r: any) => r.data ?? []),
    enabled: !!matchId && !!teamId && historyOpen,
  });

  const saveFormationMut = useMutation({
    mutationFn: (body: { template?: string; startingLineup: any[]; bench?: any[] }) =>
      endpoints
        .saveFormation(matchId!, body)
        .then((r) => r.data),
    onSuccess: () => {
      invalidateMatch();
      setHistoryOpen(false);
    },
  });

  const applyHistoricalFormation = (hist: any) => {
    saveFormationMut.mutate({
      template: hist.formation?.template,
      startingLineup: hist.formation?.startingLineup ?? [],
      bench: hist.formation?.bench ?? [],
    });
  };

  const saveAsTemplateMut = useMutation({
    mutationFn: (templateName: string) =>
      endpoints.saveMatchAsTemplate(matchId!, templateName).then((r) => r.data),
    onSuccess: invalidateMatch,
  });

  const handleSaveAsTemplate = () => {
    const name = window.prompt(
      'Nome template formazione:',
      `Formazione ${new Date().toLocaleDateString('it-IT')}`,
    );
    if (!name?.trim()) return;
    saveAsTemplateMut.mutate(name.trim());
  };

  const handleApplyTemplate = (templateId: string) => {
    const tpl = FORMATION_TEMPLATES.find((t) => t.id === templateId);
    if (!tpl) return;
    const confirmed = starters.length === 0 ||
      window.confirm(
        `Applicare template "${tpl.label}"? I giocatori attuali resteranno ma verranno riposizionati.`,
      );
    if (!confirmed) return;

    const existingStarters = starters;
    const newLineup = tpl.positions.map((pos, i) => {
      const existing = existingStarters[i];
      return {
        athleteId: existing?.athleteId ?? '',
        positionId: pos.id,
        shirtNumber: existing?.shirtNumber,
        roleLabel: pos.roleLabel,
        customPosition: { x: pos.x, y: pos.y },
      };
    }).filter((p) => p.athleteId);

    saveFormationMut.mutate({
      template: tpl.id,
      startingLineup: newLineup,
      bench: bench,
    });
  };

  // ─────────────────── Render ───────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.centered}>
          <LeopartLoader size="md" />
        </div>
      </div>
    );
  }

  if (error || !match) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.centered}>
          <p className={styles.errorText}>Partita non trovata</p>
        </div>
      </div>
    );
  }

  const formation = match.formation ?? {};
  const starters: Array<{
    athleteId: string;
    positionId?: string;
    shirtNumber?: number;
    roleLabel?: string;
    customPosition?: { x: number; y: number };
    name?: string;
  }> = (formation.startingLineup ?? []).map((p: any) => ({
    ...p,
    name:
      teamPlayers.find((tp) => tp._id === p.athleteId)?.lastName ??
      p.athleteId,
  }));

  const bench: Array<{
    athleteId: string;
    covers?: string[];
    priority?: number;
  }> = formation.bench ?? [];

  const matchDate = match.scheduledDate
    ? new Date(match.scheduledDate).toLocaleDateString("it-IT", {
        weekday: "short",
        day: "numeric",
        month: "short",
      })
    : "";

  const spaceTypeRaw = (teamData as any)?.spaceId?.type as string | undefined;
  const sport: 'soccer' | 'rugby' | 'basket' | 'volleyball' | 'futsal' =
    spaceTypeRaw === 'rugby-union'
      ? 'rugby'
      : spaceTypeRaw === 'basket'
        ? 'basket'
        : spaceTypeRaw === 'volley-ball'
          ? 'volleyball'
          : 'soccer';

  const getPlayerName = (id: string) => {
    const p = teamPlayers.find((tp) => tp._id === id);
    return p ? `${p.firstName} ${p.lastName}` : id;
  };

  const subsWithNames = (match.substitutions ?? []).map((s: any) => ({
    ...s,
    playerInName: s.playerIn ? getPlayerName(s.playerIn) : undefined,
    playerOutName: s.playerOut ? getPlayerName(s.playerOut) : undefined,
  }));

  const minutesWithNames = (match.minutesPlayed ?? []).map((m: any) => ({
    ...m,
    name: getPlayerName(m.athleteId),
  }));

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.inner}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <button
                className={styles.backBtn}
                onClick={() => navigate(-1)}
                type="button"
                aria-label="Torna indietro"
              >
                <ArrowLeft size={20} />
              </button>
              <div className={styles.titleBlock}>
                <p className={styles.matchTitle}>
                  {match.opponentName
                    ? `vs ${match.opponentName}`
                    : "Partita"}
                  {match.competition ? ` · ${match.competition}` : ""}
                </p>
                <p className={styles.matchSubtitle}>
                  {[
                    matchDate,
                    match.kickoffTime,
                    match.venue === "home"
                      ? "Casa"
                      : match.venue === "away"
                      ? "Trasferta"
                      : match.venue,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            </div>
            <button
              className={styles.backBtn}
              type="button"
              aria-label="Esporta PDF"
              title="Esporta PDF"
            >
              <Export size={18} />
            </button>
          </div>

          {/* Tab strip */}
          <div className={styles.tabsRow}>
            <div className={styles.tabGroup}>
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={[
                      styles.tabBtn,
                      isActive ? styles.tabBtnActive : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    type="button"
                  >
                    <p
                      className={[
                        styles.tabLabel,
                        isActive ? styles.tabLabelActive : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {TAB_CONFIG[tab].label}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab: FORMAZIONE */}
          {activeTab === "formazione" && (
            <>
              {/* Storico formazioni stagione (spec v1.1.1 sez 9 righe 781-791) */}
              <div className={styles.lineupHistoryRow}>
                <button
                  type="button"
                  className={styles.historyBtn}
                  onClick={() => setHistoryOpen((v) => !v)}
                >
                  <ClockCounterClockwise size={14} />
                  Formazioni precedenti
                </button>
                <button
                  type="button"
                  className={styles.historyBtn}
                  onClick={handleSaveAsTemplate}
                  disabled={saveAsTemplateMut.isPending || starters.length === 0}
                  title={
                    match.savedAsTemplate
                      ? `Già template: "${match.templateName}"`
                      : 'Salva formazione come template riusabile'
                  }
                  style={{
                    marginLeft: 8,
                    opacity: match.savedAsTemplate ? 0.6 : 1,
                  }}
                >
                  ★ {match.savedAsTemplate ? 'Template' : 'Salva come template'}
                </button>

                <select
                  className={styles.templateSelect}
                  defaultValue=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) handleApplyTemplate(v);
                    e.target.value = '';
                  }}
                >
                  <option value="">Applica template predefinito...</option>
                  {getTemplatesForSport(sport).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>
                {historyOpen && lineupHistory.length > 0 && (
                  <div className={styles.historyDropdown}>
                    {lineupHistory.slice(0, 5).map((h: any) => (
                      <div key={h._id} className={styles.historyItem}>
                        <div className={styles.historyItemLeft}>
                          <div className={styles.historyItemTitle}>
                            {h.opponentName ?? 'Avversario'} ·{' '}
                            {h.scheduledDate
                              ? new Date(h.scheduledDate).toLocaleDateString('it-IT')
                              : '—'}
                          </div>
                          <div className={styles.historyItemMeta}>
                            {h.result?.outcome === 'W'
                              ? 'Vittoria'
                              : h.result?.outcome === 'L'
                                ? 'Sconfitta'
                                : h.result?.outcome === 'D'
                                  ? 'Pareggio'
                                  : '—'}
                            {h.result?.homeScore != null &&
                              h.result?.awayScore != null &&
                              ` · ${h.result.homeScore}-${h.result.awayScore}`}
                            {h.formation?.startingLineup &&
                              ` · ${h.formation.startingLineup.length} titolari`}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={styles.historyApplyBtn}
                          disabled={saveFormationMut.isPending}
                          onClick={() => applyHistoricalFormation(h)}
                          title="Duplica come base"
                        >
                          <CopySimple size={12} /> Duplica
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {historyOpen && lineupHistory.length === 0 && (
                  <div className={styles.historyEmpty}>
                    Nessuna formazione precedente trovata
                  </div>
                )}
              </div>

              <div className={styles.threeCol}>
              {/* Colonna TITOLARI */}
              <div className={styles.colPanel}>
                <div className={styles.colHeader}>
                  Titolari ({starters.length})
                </div>
                <div className={styles.colBody}>
                  {starters.map((p) => (
                    <PlayerCard
                      key={p.athleteId}
                      athleteId={p.athleteId}
                      name={p.name ?? p.athleteId}
                      shirtNumber={p.shirtNumber}
                      roleLabel={p.roleLabel ?? p.positionId}
                      compact
                    />
                  ))}
                </div>
                <div className={styles.colFooter}>
                  {starters.length} titolari
                </div>
              </div>

              {/* Campo centrale */}
              <div className={[styles.colPanel, styles.fieldContainer].join(" ")}>
                <FormationBoard
                  sport={sport}
                  startingLineup={starters}
                />
              </div>

              {/* Colonna PANCHINA */}
              <div className={styles.colPanel}>
                <div className={styles.colHeader}>
                  Panchina ({bench.length})
                </div>
                <div className={styles.colBody}>
                  {bench.map((p) => {
                    const name = getPlayerName(p.athleteId);
                    return (
                      <PlayerCard
                        key={p.athleteId}
                        athleteId={p.athleteId}
                        name={name}
                        roleLabel={p.covers?.join(", ")}
                        compact
                      />
                    );
                  })}
                </div>
                <div className={styles.colFooter}>
                  {bench.length} riserve
                </div>
              </div>
              </div>
            </>
          )}

          {/* Tab: CAMBI */}
          {activeTab === "cambi" && (
            <div className={styles.tabContent}>
              <SubstitutionsTab
                substitutions={subsWithNames}
                onAddSubstitution={(sub) => addSubstitutionMut.mutateAsync(sub)}
                availablePlayers={teamPlayers}
                isLoading={addSubstitutionMut.isPending}
              />
            </div>
          )}

          {/* Tab: POST-GARA */}
          {activeTab === "post-gara" && (
            <div className={styles.tabContent}>
              <PostMatchTab
                matchResult={match.result}
                minutesPlayed={minutesWithNames}
                postMatchNotes={match.postMatchNotes}
                totalMinutes={90}
                sport={sport}
                onSave={(data) => savePostMatchMut.mutateAsync(data)}
                onRecalc={(mins) => recalcMut.mutateAsync(mins)}
                isLoading={savePostMatchMut.isPending || recalcMut.isPending}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchPage;
